/**
 * Date display — DATE vs DATETIME vs TIME.
 * Date-only business values must not shift day via UTC conversion.
 */

export type FormatDateOptions = {
  /** Default PRINT: DD-MM-YYYY (quotation / commercial print today). */
  style?: 'print_dmY' | 'iso' | 'long_en_US';
  nullDisplay?: string;
};

export type FormatDateTimeOptions = {
  locale?: string;
  /** Preserve prior browser/toLocaleString behavior when unspecified. */
  nullDisplay?: string;
};

/** Extract YYYY-MM-DD calendar parts without timezone shift. */
export function parseDateOnlyParts(
  value: string | Date | null | undefined
): { y: number; m: number; d: number } | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
  }
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  }
  // Fallback: local calendar from Date parse (legacy dayjs-like paths)
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Business / print dates. Prefer date-only parsing for ISO date prefixes.
 * Matches historical quotation print `DD-MM-YYYY` for date-only strings.
 */
export function formatDate(value: string | Date | null | undefined, options: FormatDateOptions = {}): string {
  const nullDisplay = options.nullDisplay ?? '';
  const parts = parseDateOnlyParts(value);
  if (!parts) return nullDisplay;
  const style = options.style ?? 'print_dmY';
  if (style === 'iso') {
    return `${parts.y}-${pad2(parts.m)}-${pad2(parts.d)}`;
  }
  if (style === 'long_en_US') {
    // Construct local noon date to avoid DST edge for month name formatting
    const dt = new Date(parts.y, parts.m - 1, parts.d, 12, 0, 0);
    return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  return `${pad2(parts.d)}-${pad2(parts.m)}-${parts.y}`;
}

/** Timestamps / audit — uses locale datetime; does not rewrite timezone architecture. */
export function formatDateTime(
  value: string | Date | null | undefined,
  options: FormatDateTimeOptions = {}
): string {
  if (value == null || value === '') return options.nullDisplay ?? '';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return options.nullDisplay ?? '';
  try {
    return dt.toLocaleString(options.locale);
  } catch {
    return dt.toISOString();
  }
}

export function formatTime(
  value: string | Date | null | undefined,
  options: FormatDateTimeOptions = {}
): string {
  if (value == null || value === '') return options.nullDisplay ?? '';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return options.nullDisplay ?? '';
  try {
    return dt.toLocaleTimeString(options.locale);
  } catch {
    return dt.toISOString().slice(11, 19);
  }
}
